import { useBoolean, useToast } from '@chakra-ui/react'
import * as crypto from 'crypto-js'
import dayjs from 'dayjs'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import useSessions from '@hook/shared/use-sessions'

import { formattedDate } from '@helper/kpi/logistic/formattedDateKpi'
import { actionsRuptureHeaderKpi } from '@helper/kpi/rupture/actionsRuptureHeaderKpi'
import { formatParamsModalKpiToSend } from '@helper/kpi/rupture/formatParamsModalKpiToSend'
import { paramsToSendMapping } from '@helper/kpi/rupture/paramsToSendMapping'
import { parseRupturaKpiExportData } from '@helper/kpi/rupture/parseRupturaKpiExportData'
import { parseRuptureKpiData } from '@helper/kpi/rupture/parseRuptureKpiData'
import { parseRuptureKpiDataModal } from '@helper/kpi/rupture/parseRuptureKpiDataModal'
import { validateGroupName } from '@helper/kpi/rupture/validateGroupName'
import { validateIfShowColumnInTable } from '@helper/kpi/rupture/validateIfShowColumnInTable'
import {
  decrypterHeaderStorange,
  encrypteHeaderStorange
} from '@helper/util/cryptoStorange'
import generic from '@service/generic'
import kpi from '@service/kpi'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader,
  Pagination
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IDefaultDate,
  IGraphicResponse,
  IkpiModalTableStructure,
  IKpiRuptureContext,
  ILineParams,
  IParamsToSend,
  IParseParamsModalOrGraphic,
  IUpdateParams
} from '@base/@types/providers/Kpi/RuptureProvider'
import { ruptureFieldsFilter } from '@base/variables/headers/kpi/rupture/ruptureFieldsFilter'
import { ruptureIndicatorsRequest } from '@base/variables/headers/kpi/rupture/ruptureIndicatorsRequest'
import { kpiRuptureHeader } from '@base/variables/headers/kpi/rupture/ruptureKpiHeader'
import { ruptureKpiModalTableStructure } from '@base/variables/headers/kpi/rupture/ruptureKpiModalTableStructure'

const KpiRuptureProvider: FC = ({ children }) => {
  const toast = useToast()
  const { register } = useSessions()
  const filtersList: ILayoutFilter[] = ruptureFieldsFilter

  const [query, setQuery] = useState('')
  const [currentyDate, setCurrentyDate] = useState({} as IDefaultDate)
  const [loadingGraphic, setLoadingGraphic] = useState(false)
  const [graphicValues, setGraphicValues] = useState({} as IGraphicResponse)
  const [isShowContentChart, setIsShowContentChart] = useState(false)
  const [isGraphic, setIsGraphic] = useState(false)
  const [isModal, setIsModal] = useState(false)
  const [isLoadingExport, setIsLoadingExport] = useState(false)
  const [isModalConfig, setIsModalConfig] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const [loading, setLoading] = useState(false)
  const [fetching, _setFetching] = useState(false)
  const [selected, setSelected] = useState(false)
  const [stateLineData, setStateLineData] = useState({} as ILineParams)
  const [filename, setFilename] = useState('KPI Ruptura')
  const [loadingModal, setLoadingModal] = useState(false)
  const [data, updateData] = useImmer<any[]>([])
  const [modalData, updateModalData] = useImmer<any[]>([])
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(kpiRuptureHeader)
  const [params, updateParams] = useImmer<any>(ruptureIndicatorsRequest)
  const [paramsModalToSend, setParamsModalToSend] = useState<IParamsToSend>()

  const [paramsGraphicToSend, setParamsGraphicToSend] =
    useState<IParamsToSend>()

  const [modalHeaders, updateModalHeaders] = useState<
    IkpiModalTableStructure[]
  >(ruptureKpiModalTableStructure)

  const defaultDate = useMemo(() => {
    const initialDate = dayjs(new Date()).format('YYYY-MM-DD')
    const finalDate = dayjs(new Date()).format('YYYY-MM-DD')

    setCurrentyDate({
      initialDate,
      finalDate
    })

    return {
      initialDate,
      finalDate
    } as IDefaultDate
  }, [])

  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...ruptureIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }
    return paramsFormatted
  })

  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })

  const [paginationMain, updatePaginationMain] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const updateIsModalConfig = () => {
    setIsModalConfig(!isModalConfig)
  }

  const getDataGraphic = useCallback(
    (paramsToSendFormatted: IParamsToSend) => {
      setIsShowContentChart(true)
      setLoadingGraphic(true)
      kpi
        .postRuptureIndicatorGraphicData(
          paramsToSendFormatted || paramsToSend,
          0
        )
        .then(({ data: { data } }) => {
          setGraphicValues({
            ruptura: data.zeroDias,
            rupturaDezDias: data.dezDias
          } as IGraphicResponse)
          setLoadingGraphic(false)
        })
        .catch(({ response: { data } }) => {
          setIsGraphic(false)
          setLoadingGraphic(false)
          setIsShowContentChart(false)
          setGraphicValues({
            ruptura: []
          } as IGraphicResponse)

          const isData = data.message === 'Nenhum dado encontrado'

          toast({
            title: isData ? 'Dados não encontrados.' : data.message,
            description: 'Não há dados para que possa ser montado o gráfico',
            status: isData ? 'info' : 'error',
            duration: 4000,
            isClosable: true,
            position: 'top-right'
          })
        })
    },
    [paramsToSend, toast]
  )

  const handlePageChange = useCallback(
    async (page: number, paramsToSendFormatted?: IParamsToSend) => {
      setLoadingModal(true)

      kpi
        .getRuptureIndicatorsProductData(
          paramsToSendFormatted || paramsModalToSend,
          page
        )
        .then(({ data: { data, pagination } }) => {
          updatePagination(pagination)
          setIsModal(true)
          updateModalData(data)
          setLoadingModal(false)
        })
        .catch(() => setLoadingModal(false))
    },
    [paramsModalToSend, updateModalData, updatePagination]
  )

  const getDataModal = useCallback(
    (paramsToSendFormatted: IParamsToSend) => {
      setParamsGraphicToSend(paramsToSendFormatted)
      setParamsModalToSend(paramsToSendFormatted)
      handlePageChange(1, paramsToSendFormatted)
    },
    [handlePageChange]
  )

  const parseParamsModalOrGraphic = ({
    isClickLine,
    lineData
  }: IParseParamsModalOrGraphic) => {
    if (localStorage.getItem('@kpiRuptureParamsToSend:')) {
      const paramsToSendSaved = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('@kpiRuptureParamsToSend:'),
          'CHAVE'
        ).toString(crypto.enc.Utf8)
      )

      const paramsToSendFormatted = formatParamsModalKpiToSend(
        lineData || stateLineData,
        paramsToSendSaved || paramsToSend
      )

      if (!isClickLine) {
        return getDataModal(paramsToSendFormatted)
      }

      getDataGraphic(paramsToSendFormatted)
    }
  }

  const updateIsGraphic = (lineData: ILineParams) => {
    setIsGraphic(true)
    setStateLineData(lineData)

    parseParamsModalOrGraphic({
      isClickLine: true,
      lineData
    })
  }

  const handleSelected = (lineData: ILineParams) => {
    setSelected(!!lineData)
    setStateLineData(lineData)
    setLoadingModal(true)

    if (lineData) {
      parseParamsModalOrGraphic({
        isClickLine: false,
        lineData
      })
    }
  }

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        icon: <AiOutlineBarChart />,
        onSelect: handleSelected
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tableInteractionModal = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'icon',
        onFocus: true,
        icon: <AiOutlineBarChart />,
        onSelect: () => []
      }
    ]
  }, [])

  const clearAllFilters = () => {
    const paramsFormatted = { ...ruptureIndicatorsRequest }

    paramsFormatted.periodo = {
      agrupar: paramsFormatted.periodo.agrupar,
      inicio: formattedDate(defaultDate.initialDate),
      final: formattedDate(defaultDate.finalDate)
    }

    updateParams({
      ...params,
      empresaIds: false,
      empresaSelectIds: [],
      centroCdLojaIds: false,
      centroCdLojaSelectIds: [],
      gerenteCategoriaIds: false,
      gerenteCategoriaSelectIds: [],
      gerenteProdutoIds: false,
      gerenteProdutoSelectIds: [],
      mundoSelectIds: [],
      categoriaCompactaIds: false,
      categoriaCompactaSelectIds: [],
      categoriaIds: false,
      categoriaSelectIds: [],
      statusIds: false,
      statusSelectIds: [],
      curvaAbcIds: false,
      curvaAbcSelectIds: [],
      fornecedorSelectIds: [],
      periodoIds: false
    })

    setParamsToSend(paramsFormatted)

    setCurrentyDate({
      initialDate: defaultDate.initialDate,
      finalDate: defaultDate.finalDate
    })
    if (localStorage.getItem('kpiRuptureParams')) {
      localStorage.setItem(
        'kpiRuptureParams',
        crypto.AES.encrypt(
          JSON.stringify({
            ...params,
            empresaIds: false,
            empresaSelectIds: [],
            centroCdLojaIds: false,
            centroCdLojaSelectIds: [],
            gerenteCategoriaIds: false,
            gerenteCategoriaSelectIds: [],
            gerenteProdutoIds: false,
            gerenteProdutoSelectIds: [],
            mundoSelectIds: [],
            categoriaCompactaIds: false,
            categoriaCompactaSelectIds: [],
            categoriaIds: false,
            categoriaSelectIds: [],
            statusIds: false,
            statusSelectIds: [],
            curvaAbcIds: false,
            curvaAbcSelectIds: [],
            fornecedorSelectIds: [],
            periodoIds: false
          }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('kpiRuptureParamsRequest')) {
      localStorage.setItem(
        'kpiRuptureParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsFormatted), 'CHAVE').toString()
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })
    name === 'initialDate' &&
      setCurrentyDate({ initialDate: value, finalDate: currentyDate.finalDate })

    name === 'finalDate' &&
      setCurrentyDate({
        finalDate: value,
        initialDate: currentyDate.initialDate
      })

    const paramsToSendFormatted = paramsToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)
    if (localStorage.getItem('kpiRuptureParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiRuptureParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      localStorage.setItem(
        'kpiRuptureParams',
        crypto.AES.encrypt(
          JSON.stringify({ ...newParams, [name]: value }),
          'CHAVE'
        ).toString()
      )
    }
    if (localStorage.getItem('kpiRuptureParamsRequest')) {
      localStorage.setItem(
        'kpiRuptureParamsRequest',
        crypto.AES.encrypt(
          JSON.stringify(paramsToSendFormatted),
          'CHAVE'
        ).toString()
      )
    }
  }
  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('kpiRuptureParams')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiRuptureParams'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )

      const newV2Params = {
        ...newParams,
        periodo: {
          inicio: currentyDate.initialDate,
          final: currentyDate.initialDate,
          agrupar: true
        }
      }
      localStorage.setItem(
        'kpiRuptureParams',
        crypto.AES.encrypt(JSON.stringify(newV2Params), 'CHAVE').toString()
      )
      updateParams(newV2Params)
    } else {
      localStorage.setItem(
        'kpiRuptureParams',
        crypto.AES.encrypt(JSON.stringify(params), 'CHAVE').toString()
      )
    }
    if (localStorage.getItem('kpiRuptureParamsRequest')) {
      const newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiRuptureParamsRequest'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      const newV2Params = {
        ...newParams,

        periodo: {
          inicio: currentyDate.initialDate,
          final: currentyDate.initialDate,
          agrupar: true
        }
      }
      localStorage.setItem(
        'kpiRuptureParamsRequest',
        crypto.AES.encrypt(JSON.stringify(newV2Params), 'CHAVE').toString()
      )
      setParamsToSend(newV2Params)
    } else {
      localStorage.setItem(
        'kpiRuptureParamsRequest',
        crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend, currentyDate])

  const handleStorange = useCallback(() => {
    if (localStorage.getItem('@kpiRuptureColumnsTable:')) {
      const newHeader = decrypterHeaderStorange('@kpiRuptureColumnsTable:')

      updateHeaders(newHeader)
    }
  }, [updateHeaders])

  const handleHeaderLocalStorange = useCallback(() => {
    if (!localStorage.getItem('@kpiRuptureColumnsTable:')) {
      encrypteHeaderStorange('@kpiRuptureColumnsTable:', headers)
    } else {
      localStorage.removeItem('@kpiRuptureColumnsTable:')
      encrypteHeaderStorange('@kpiRuptureColumnsTable:', headers)
    }
  }, [headers])

  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show,
        notShowColumn: newHeaders[index].columns[column].notShowColumn
          ? !newHeaders[index].columns[column].notShowColumn
          : true,
        isNumeric: newHeaders[index].columns[column].isNumeric
      }

      updateHeaders(newHeaders)
      handleHeaderLocalStorange()
    },
    [handleHeaderLocalStorange, headers, updateHeaders]
  )

  const updateModalColumnsAndSaveState = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...modalHeaders]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateModalHeaders(newHeaders)
    },
    [modalHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return actionsRuptureHeaderKpi({
      data,
      headers,
      toggleColumn,
      parseRuptureKpiData
    })
  }, [toggleColumn, data, headers])

  const updateShowTableInTable = (data: any[]) => {
    updateData(draft => {
      return draft.filter(item => item.status === 99).concat(data)
    })

    const headersFormatted = validateIfShowColumnInTable(headers, paramsToSend)

    handleStorange()
    updateHeaders(headersFormatted)
  }

  const handlePageChangeMain = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        'kpiRuptureParamsRequest:',
        crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
      )
      let newParams = {}
      if (localStorage.getItem('kpiRuptureParamsRequest')) {
        newParams = JSON.parse(
          crypto.AES.decrypt(
            localStorage.getItem('kpiRuptureParamsRequest'),
            'CHAVE'
          ).toString(crypto.enc.Utf8) || ''
        )
      } else {
        newParams = paramsToSend
      }
      kpi
        .getRuptureIndicatorsData(newParams, _page)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePaginationMain(pagination)
          setLoading(false)
        })
        .catch(() => {
          updateData([])
          setLoading(false)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [paramsToSend, updateData, updatePagination]
  )

  const getData = () => {
    setGraphicValues({ ruptura: [], rupturaDezDias: [] })
    setIsShowContentChart(false)
    setIsGraphic(false)
    getMaintence()

    setLoading(true)
    localStorage.setItem(
      '@kpiRuptureParamsToSend:',
      crypto.AES.encrypt(JSON.stringify(paramsToSend), 'CHAVE').toString()
    )
    let newParams = {}
    if (localStorage.getItem('kpiRuptureParamsRequest')) {
      newParams = JSON.parse(
        crypto.AES.decrypt(
          localStorage.getItem('kpiRuptureParamsRequest'),
          'CHAVE'
        ).toString(crypto.enc.Utf8) || ''
      )
      newParams = {
        ...newParams,

        periodo: paramsToSend.periodo
      }
    } else {
      newParams = paramsToSend
    }
    kpi
      .getRuptureIndicatorsData(newParams, 1)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePaginationMain(pagination)

        localStorage.setItem(
          '@kpiRuptureParamsResponse:',
          crypto.AES.encrypt(JSON.stringify(data), 'CHAVE').toString()
        )

        register({
          name: 'KPI Ruptura',
          path: '/kpi/ruptura',
          loading: false
        })
        setFilters.toggle()

        setLoading(false)
      })
      .catch(() => {
        updateData([])
        setLoading(false)
      })
  }

  const exportKpiData = useCallback(() => {
    Swal.fire({
      title: 'Exporta dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const paramsToSendFormatted = parseRupturaKpiExportData(
      headers,
      paramsToSend
    )

    kpi
      .postRuptureIndicatorExportData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, filename + '.csv')
        Swal.close()
      })
      .catch(({ response: { data } }) => {
        const message =
          data.response?.status === 400
            ? data.response?.data?.data[0].message
            : 'Ocorreu um problema interno. Tente novamente mais tarde'
        toast({
          title: 'Atenção.',
          description: message,
          status: 'warning',
          duration: 3000,
          isClosable: true
        })
      })
  }, [filename, headers, paramsToSend, toast])

  const exportKpiDataModal = useCallback(() => {
    setIsLoadingExport(true)

    const formatDate = (dateStr?: string) => {
      if (!dateStr) return dateStr
      // já está no formato yyyy-mm-dd → retorna direto
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr
      }
      // se vier no formato dd/mm/yyyy → converte
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
        const [day, month, year] = dateStr.split('/')
        return `${year}-${month}-${day}`
      }
      return dateStr
    }

    const paramsToSendFormatted = {
      ...paramsGraphicToSend,
      periodo: {
        ...paramsGraphicToSend.periodo,
        inicio: formatDate(paramsGraphicToSend.periodo?.inicio),
        final: formatDate(paramsGraphicToSend.periodo?.final)
      }
    }

    kpi
      .postRuptureIndicatorExportModalData(paramsToSendFormatted)
      .then(({ data }) => {
        fileDownload(data, 'Kpi-Ruptura Produtos.csv')
        setIsLoadingExport(false)
      })
      .catch(() => {
        setIsLoadingExport(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, paramsGraphicToSend])
  const getMaintence = useCallback(async () => {
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        if (data.valor === 'true') {
          return (window.location.href = '/manutencao')
        }
      })
      .catch(() => {})
  }, [])

  return (
    <KpiRuptureContext.Provider
      value={{
        data: parseRuptureKpiData(data),
        query,
        params,
        isModal,
        headers,
        actions,
        filters,
        handleLocalParams,
        loading,
        fetching,
        filename,
        selected,
        defaultDate,
        currentyDate,
        filtersList,
        tableInteraction,
        getData,
        setQuery,
        setFilters,
        setFilename,
        exportKpiData,
        clearAllFilters,
        handleUpdateParams,

        loadingModal,
        isModalConfig,
        isLoadingExport,
        isModalOpen: selected,
        headersModal: modalHeaders,
        actionsModal: tableInteractionModal,
        kpiModalTableStructure: modalHeaders,
        dataModal: parseRuptureKpiDataModal(modalData),
        exportKpiDataModal,
        updateIsModalConfig,
        onModalClose: () => setSelected(!selected),
        onModalChange: updateModalColumnsAndSaveState,

        pagination,
        handlePageChange,

        paginationMain,
        handlePageChangeMain,

        isGraphic,
        graphicValues,
        loadingGraphic,
        isShowContentChart,
        setIsGraphic,
        updateIsGraphic
      }}
    >
      {children}
    </KpiRuptureContext.Provider>
  )
}

export default KpiRuptureProvider
export const KpiRuptureContext = createContext<IKpiRuptureContext>(
  {} as IKpiRuptureContext
)
